<template>
    <div>
        <v-card outlined v-show="showAlert" class="v-card-border-none">
            <v-card-title class="pt-2 pb-2 v-card-custom-title">
                <div
                    class="text-left v-card-custom-title-left"
                    style="width: 60%"
                >
                    <template v-if="!VUETIFY_BREAKPOINT.mobile">
                        <span style="white-space: nowrap"
                            >{{ $t("alert.title.LargePNL")
                            }}<v-tooltip right v-if="configWarning">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                        v-bind="attrs"
                                        v-on="on"
                                        color="red"
                                        small
                                        style="margin-left: 2px"
                                        >mdi-information-outline</v-icon
                                    >
                                </template>
                                <span>{{
                                    $t("alert.noConfigFound.LargePNL")
                                }}</span>
                            </v-tooltip></span
                        >
                    </template>
                </div>
                <div
                    class="text-right v-card-custom-title-right"
                    style="width: 40%"
                >
                    <template v-if="!VUETIFY_BREAKPOINT.mobile">
                        <download-excel
                            :data="filteredAlertData"
                            :fields="csvHeader"
                            style="display: inline"
                            type="csv"
                            :name="$t('alert.exportName.LargePNL') + '.csv'"
                            class="alertDownload"
                            :escapeCsv="false"
                        >
                            <v-btn
                                icon
                                x-small
                                :disabled="filteredAlertData.length === 0"
                                color="green"
                                style="margin-right: 2px"
                                ><v-icon>mdi-microsoft-excel</v-icon>
                            </v-btn>
                        </download-excel>
                        <v-btn
                            icon
                            x-small
                            color="blue-grey darken-1"
                            v-on:click="getConfigForAlertAction('Large PNL')"
                            style="margin-right: 2px"
                            v-if="showConfig"
                            class="alertConfig"
                            ><v-icon>mdi-cog</v-icon>
                        </v-btn>
                        {{ " " }}
                        <v-icon
                            small
                            style="color: grey"
                            @click="$emit('onRemove')"
                            class="alertClose"
                            >mdi-close-thick</v-icon
                        >
                    </template>
                </div>
            </v-card-title>

            <v-data-table
                dense
                item-key="name"
                :customSort="customSort"
                :items="filteredAlertData"
                :headers="alertHeader"
                class="elevation-0 alert-datatable"
                :items-per-page.sync="offset"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :height="height - 120"
                fixed-header
                :header-props="{ sortIcon: null }"
                :page.sync="currentPage"
                :mobile-breakpoint="0"
                :footer-props="{
                    'items-per-page-text': '',
                    'items-per-page-options': [30, 50, 100, 300, 500, 1000],
                }"
            >
                <template v-slot:item="{ item }">
                    <tr v-on:click="rowClicked(item)">
                        <td column="login">
                            <a
                                v-on:click.stop="handleLoginClick(item)"
                                v-if="showHomeLoginDetailSummary"
                                style="display: inline-block; height: 21px"
                                >{{ item.login }}</a
                            >

                            <div v-else>
                                {{ item.login }}
                            </div>
                            <v-icon v-show="item.new == true"
                                >mdi-new-box</v-icon
                            >
                        </td>
                        <td
                            column="name"
                            class="truncate-name-cell"
                            v-if="
                                selectedProfile.settings['largePNL'].name
                                    .showing
                            "
                        >
                            {{ decodeMaybeHexOrGarbled(item.name) }}
                        </td>
                        <td
                            column="books"
                            v-if="
                                selectedProfile.settings['largePNL'].books
                                    .showing
                            "
                        >
                            {{ item.books }}
                        </td>
                        <td
                            column="ccy"
                            v-if="
                                selectedProfile.settings['largePNL'].ccy.showing
                            "
                        >
                            {{ item.ccy }}
                        </td>
                        <td
                            column="today_open_trades_profit"
                            align="right"
                            v-if="
                                selectedProfile.settings['largePNL']
                                    .today_open_trades_profit.showing
                            "
                        >
                            {{
                                numberWithCommas(
                                    item.today_open_trades_profit.toFixed(2)
                                )
                            }}
                        </td>
                        <td
                            column="top_symbol"
                            v-if="
                                selectedProfile.settings['largePNL'].top_symbol
                                    .showing
                            "
                        >
                            {{ item.top_symbol }}
                        </td>
                        <td
                            column="trigger_time"
                            v-if="
                                selectedProfile.settings['largePNL']
                                    .trigger_time.showing
                            "
                        >
                            {{ item.trigger_time }}
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>

        <LoginSummaryDialog
            v-if="dialog"
            :fullLogin="fullLoginValue"
            :login="loginValue"
            :server="serverValue"
            :symbol="symbolValue"
        />
    </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import LoginSummaryDialog from "@components/login-summary/ComponentLoginSummaryDialog";
import { helper } from "@components/mixins/helper";
import common from "@assets/js/common";
import deepClone from "deep-clone";
import { permission } from "@components/mixins/permission";
import { getConfig } from "@services/alert/config";
import { redirectToRH } from "@utils/util";

export default {
    mixins: [helper, permission],
    components: { LoginSummaryDialog },
    props: {
        height: 0,
        showing: false,
        showConfig: false,
    },
    data() {
        return {
            visible: false,
            showAlert: true,
            showMonitor: false,
            dialog: false,
            sortBy: "lockingVolume",
            sortDesc: false,
            recordDate: common.getMT4TimeString().split(" ")[0],
            showHomeLoginDetailSummary: false,
            fullLoginValue: null,
            loginValue: null,
            serverValue: null,
            symbolValue: null,
            offset: 30,
            currentPage: 1,
            configWarning: false,
        };
    },
    computed: {
        ...mapState("LargePNL", [
            "alertHeader",
            "alertData",
            "loading",
            "csvHeader",
        ]),
        ...mapState("Alert", ["selectedProfile", "selectedPeriod", "books"]),
        ...mapState("SettingsServer", ["serverData"]),
        selectedBooks() {
            return (
                this.books.filter(
                    (book) =>
                        !this.selectedProfile.unselectedBooks.includes(book)
                ) || []
            );
        },
        filteredAlertData() {
            if (!!this.selectedProfile.unselectedBooks) {
                if (this.selectedProfile.unselectedBooks.length === 0) {
                    return this.bookSelectionFilterData;
                } else {
                    const filtered = [];
                    this.bookSelectionFilterData.map((data) => {
                        const dataBooks = data.books.split(",");
                        if (
                            this.haveCommonItems(this.selectedBooks, dataBooks)
                        ) {
                            filtered.push(data);
                        }
                    });
                    return filtered;
                }
            }
            return this.bookSelectionFilterData;
        },
        bookSelectionFilterData() {
            let filtered = [];
            this.alertData.map((data) => {
                const dataBooks = data.books.split(",");
                if (this.haveCommonItems(this.books, dataBooks)) {
                    filtered.push(data);
                }
            });
            return filtered;
        },
    },
    watch: {
        showing(nv) {
            if (nv) {
                getConfig("large-pnl").then(({ data }) => {
                    if (data.length === 0) {
                        this.configWarning = true;
                    }
                });
            }
        },
        selectedProfile(nv) {
            const settings = nv.settings["largePNL"];
            const columnName = Object.keys(settings);
            let newHeader = deepClone(this.alertHeader);
            newHeader.map((item) => {
                if (columnName.includes(item.value)) {
                    item.align = settings[item.value].showing
                        ? ["today_open_trades_profit"].includes(item.value)
                            ? "right"
                            : "left"
                        : " d-none";
                }
            });
            this.UPDATE_ALERT_HEADER(newHeader);
        },
    },
    methods: {
        ...mapMutations("LargePNL", [
            "UPDATE_ALERT_HEADER",
            "UPDATE_ALERT_DATA",
            "UPDATE_SELECTED_DATE",
        ]),
        ...mapActions("LargePNL", ["getDataLargePNLAction"]),
        ...mapActions("Config", ["getConfigForAlertAction"]),
        haveCommonItems(arr1, arr2) {
            const set1 = new Set(arr1);
            const commonItems = arr2.filter((item) => set1.has(item));
            return commonItems.length > 0;
        },
        customSort(items, sortBy, sortDesc) {
            items.sort((a, b) => {
                if (!sortDesc[0]) {
                    return a[sortBy] < b[sortBy] ? -1 : 1;
                } else {
                    return b[sortBy] < a[sortBy] ? -1 : 1;
                }
            });
            return items;
        },
        handleLoginClick(item) {
            const serverId = this.serverData.find(
                (server) => server.server_name === item.login.split(":")[0]
            ).id;
            const login = item.login;
            const url = `/tag-centre/account-analysis/${serverId}-${login}`;
            redirectToRH(url);
        },
        rowClicked(item) {
            const broker = this.serverData.find(
                (serverInfo) =>
                    serverInfo.server_name === item.login.split(":")[0]
            )?.broker_name;
            if (!!broker) {
                this.fullLoginValue = broker + ":" + item.login;
                this.loginValue = item.login.split(":")[1];
                this.serverValue = item.login.split(":")[0];
                this.symbolValue = item.top_symbol.split(".")[0];
                this.dialog = false;
                this.$nextTick(() => {
                    this.dialog = true;
                });
            }
        },
    },
    created() {
        const userFunction = this.getFunction("book");

        if (userFunction.includes("homeLoginDetailSummary")) {
            this.showHomeLoginDetailSummary = true;
        }
    },
};
</script>
